/**
 * Selling Partner API connection contracts.
 *
 * SP-API authorization is deliberately separate from Amazon Ads OAuth. These
 * schemas never expose LWA credentials or encrypted token values to clients.
 */
import { z } from "zod";

const nonEmptyOpaqueValueSchema = z.string().trim().min(1);
const isoDateTimeSchema = z.string().datetime({ offset: true });

export const spApiRegionSchema = z.enum(["na", "eu", "fe"]);
export type SpApiRegion = z.infer<typeof spApiRegionSchema>;

export const spApiConnectionStatusSchema = z.enum(["connected", "token_invalid", "revoked"]);
export type SpApiConnectionStatus = z.infer<typeof spApiConnectionStatusSchema>;

export const spApiAuthorizationKindSchema = z.enum(["public", "self_authorized"]);
export type SpApiAuthorizationKind = z.infer<typeof spApiAuthorizationKindSchema>;

export const spApiConnectionHealthSchema = z.enum([
  "connected",
  "renewal_due",
  /** Tokens still refresh, but Amazon answers SP-API calls with 403; re-authorize. */
  "authorization_denied",
  "token_invalid",
  "revoked",
]);
export type SpApiConnectionHealth = z.infer<typeof spApiConnectionHealthSchema>;

export const spApiConnectRequestSchema = z.object({}).strict();
export type SpApiConnectRequest = z.infer<typeof spApiConnectRequestSchema>;

export const spApiCallbackRequestSchema = z
  .object({
    state: nonEmptyOpaqueValueSchema.max(512),
    sellingPartnerId: nonEmptyOpaqueValueSchema.max(128),
    spapiOauthCode: nonEmptyOpaqueValueSchema.max(4096),
  })
  .strict();
export type SpApiCallbackRequest = z.infer<typeof spApiCallbackRequestSchema>;

/** Private-app bootstrap input. The refresh token must only be accepted by a protected endpoint. */
export const spApiSelfAuthorizationRequestSchema = z
  .object({
    sellingPartnerId: nonEmptyOpaqueValueSchema.max(128),
    region: spApiRegionSchema,
    refreshToken: nonEmptyOpaqueValueSchema.max(4096),
  })
  .strict();
export type SpApiSelfAuthorizationRequest = z.infer<typeof spApiSelfAuthorizationRequestSchema>;

export const spApiLinkedShopSchema = z.object({
  shopId: z.string().uuid(),
  name: z.string().min(1),
  marketplaceId: z.string().min(1),
  marketplaceName: z.string().min(1),
});
export type SpApiLinkedShop = z.infer<typeof spApiLinkedShopSchema>;

export const spApiConnectionSchema = z.object({
  id: z.string().uuid(),
  sellingPartnerId: z.string().min(1),
  region: spApiRegionSchema,
  authorizationKind: spApiAuthorizationKindSchema,
  status: spApiConnectionStatusSchema,
  health: spApiConnectionHealthSchema,
  authorizedAt: isoDateTimeSchema,
  authorizationExpiresAt: isoDateTimeSchema.nullable(),
  lastError: z.string().nullable(),
  lastErrorAt: isoDateTimeSchema.nullable(),
  linkedShops: z.array(spApiLinkedShopSchema),
});
export type SpApiConnection = z.infer<typeof spApiConnectionSchema>;

export const spApiConnectResponseSchema = z.object({
  redirectUrl: z.string().url(),
});
export type SpApiConnectResponse = z.infer<typeof spApiConnectResponseSchema>;

export const spApiCallbackResponseSchema = z.object({
  connection: spApiConnectionSchema,
});
export type SpApiCallbackResponse = z.infer<typeof spApiCallbackResponseSchema>;

export const spApiConnectionsResponseSchema = z.object({
  connections: z.array(spApiConnectionSchema),
});
export type SpApiConnectionsResponse = z.infer<typeof spApiConnectionsResponseSchema>;

export const spApiDisconnectResponseSchema = z.object({
  connectionId: z.string().uuid(),
  status: z.literal("revoked"),
});
export type SpApiDisconnectResponse = z.infer<typeof spApiDisconnectResponseSchema>;
